import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react"
import { createPortal } from "react-dom"

// Anchors a popover to a trigger element via a measured
// rect, then portals it to `document.body` so the
// popover escapes any ancestor with `overflow: hidden` or
// a non-`none` transform. CON-108 burned us when a
// transformed nav-drawer ancestor became the containing
// block for a `position: fixed` descendant — the same
// trap applies to any v2 sidebar popover. The fix is to
// portal everything; this primitive keeps that
// invariant in one place.
//
// Anchoring is rect-based, not ref-based on the trigger,
// because the portal lives outside the trigger's tree and
// we want it to react to the trigger's current viewport
// position (not its document position).

export type Placement = "top-start" | "bottom-start" | "bottom-end" | "top-end"

interface PortalPopoverProps {
  open: boolean
  onClose: () => void
  triggerRef: RefObject<HTMLElement | null>
  placement?: Placement
  // 8px default vertical gap; horizontal alignment to the
  // chosen edge.
  offset?: number
  // Width handled outside (each popover has its own w-*).
  // We don't shrink-wrap the trigger.
  className?: string
  // Stacking layer for the portaled popover. Defaults to
  // `z-40`. Pass a higher layer (e.g. `z-[60]`) when the
  // popover opens from inside a surface that sits above
  // z-40 — the slide-out drawer panel is `z-50`, so a
  // popover anchored to a row inside the drawer must be
  // above it or it renders hidden behind the panel.
  zClassName?: string
  // Aria-label applied to the dialog wrapper.
  ariaLabel?: string
  children: ReactNode
}

interface Coords {
  top: number
  left: number
  // The trigger width — handed to the consumer via CSS
  // variable so popovers that want to match the trigger
  // width can do so without re-measuring.
  triggerWidth: number
}

export function PortalPopover({
  open,
  onClose,
  triggerRef,
  placement = "bottom-start",
  offset = 8,
  className = "",
  zClassName = "z-40",
  ariaLabel,
  children,
}: PortalPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState<Coords | null>(null)

  // useLayoutEffect for the initial measurement to avoid a
  // one-frame flash where the popover appears at (0, 0)
  // before settling next to the trigger.
  useLayoutEffect(() => {
    if (!open) return
    const measure = () => {
      const trigger = triggerRef.current
      const popover = popoverRef.current
      if (!trigger || !popover) return

      const triggerRect = trigger.getBoundingClientRect()
      const popoverRect = popover.getBoundingClientRect()

      let top = 0
      let left = 0
      switch (placement) {
        case "bottom-start":
          top = triggerRect.bottom + offset
          left = triggerRect.left
          break
        case "bottom-end":
          top = triggerRect.bottom + offset
          left = triggerRect.right - popoverRect.width
          break
        case "top-start":
          top = triggerRect.top - popoverRect.height - offset
          left = triggerRect.left
          break
        case "top-end":
          top = triggerRect.top - popoverRect.height - offset
          left = triggerRect.right - popoverRect.width
          break
      }

      // Clamp to viewport with an 8px gutter so the popover
      // never crowds the screen edge when the trigger is
      // near a corner.
      const gutter = 8
      const maxLeft = window.innerWidth - popoverRect.width - gutter
      const maxTop = window.innerHeight - popoverRect.height - gutter
      left = Math.max(gutter, Math.min(left, maxLeft))
      top = Math.max(gutter, Math.min(top, maxTop))

      setCoords({
        top,
        left,
        triggerWidth: triggerRect.width,
      })
    }

    measure()
    window.addEventListener("resize", measure)

    // Re-clamp when the popover's own size changes — e.g. a panel
    // that opens at a "Loading…" height and then grows once async
    // content (teammate list, saved views) resolves. Without this
    // the popover keeps the stale top from the short first paint
    // and its footer can spill below the viewport. jsdom is
    // ResizeObserver-polyfilled in test setup, but guard anyway.
    let observer: ResizeObserver | undefined
    if (typeof ResizeObserver !== "undefined" && popoverRef.current) {
      observer = new ResizeObserver(() => measure())
      observer.observe(popoverRef.current)
    }

    return () => {
      window.removeEventListener("resize", measure)
      observer?.disconnect()
    }
  }, [open, placement, offset, triggerRef])

  // Close on outside scroll (CON-111). The legacy code
  // re-measured on every scroll event, which had two
  // failure modes seen in CON-110: scrolling the chip row
  // horizontally moved the trigger and dragged the popover
  // with it across the viewport, and any internal popover
  // scroll re-clamped against the popover's current rect
  // and produced visible drift. Standard popover UX is to
  // dismiss on outside scroll instead — the user explicitly
  // intended to leave the anchor's context, so we treat
  // the open popover as stale and close it.
  //
  // Scrolls that originate inside the popover itself
  // (a long saved-view list, the future status sub-view)
  // are exempt — those keep the popover open.
  useEffect(() => {
    if (!open) return

    const handleScroll = (event: Event) => {
      const target = event.target as Node | null
      if (!target) return
      if (popoverRef.current?.contains(target)) return
      onClose()
    }

    window.addEventListener("scroll", handleScroll, true)
    return () => window.removeEventListener("scroll", handleScroll, true)
  }, [open, onClose])

  // Outside-click + Escape dismissal. The ref covers the
  // popover only — the trigger is expected to be a
  // <button aria-expanded> that toggles itself, and
  // clicking the trigger again should close. We don't try
  // to track the trigger in our outside-click handler
  // because the trigger's onClick will fire first and
  // close us before this handler sees anything.
  useEffect(() => {
    if (!open) return

    const handleMouse = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!popoverRef.current || !target) return
      if (popoverRef.current.contains(target)) return
      // Trigger handles its own toggle on click; we just
      // need to ignore clicks on the trigger so we don't
      // close + re-open in the same gesture.
      const trigger = triggerRef.current
      if (trigger && trigger.contains(target)) return
      onClose()
    }

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }

    window.addEventListener("mousedown", handleMouse)
    window.addEventListener("keydown", handleKey)
    return () => {
      window.removeEventListener("mousedown", handleMouse)
      window.removeEventListener("keydown", handleKey)
    }
  }, [open, onClose, triggerRef])

  if (!open) return null
  if (typeof document === "undefined") return null

  // Initial render at (0, 0) with opacity-0 so the layout
  // measurement above can place us before the user sees a
  // flash. Once `coords` is set we fade in.
  const style = coords
    ? {top: coords.top, left: coords.left}
    : {top: -9999, left: -9999}

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={ariaLabel}
      style={style}
      className={`fixed ${zClassName} ${className}`}
      data-testid="portal-popover"
    >
      {children}
    </div>,
    document.body,
  )
}

// Shared bottom-sheet primitive. Same portal pattern,
// different anchoring — full-viewport overlay with a
// scrim. Use this for mobile sheets.
interface PortalSheetProps {
  open: boolean
  onClose: () => void
  ariaLabel?: string
  // Stacking layer for the portaled sheet (scrim + panel).
  // Defaults to `z-40`. Pass a higher layer (e.g. `z-[60]`)
  // when the sheet opens from inside the slide-out drawer
  // (`z-50`) so it overlays the drawer instead of rendering
  // behind it.
  zClassName?: string
  children: ReactNode
}

export function PortalSheet({
  open,
  onClose,
  ariaLabel,
  zClassName = "z-40",
  children,
}: PortalSheetProps) {
  useEffect(() => {
    if (!open) return
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleKey)
    return () => window.removeEventListener("keydown", handleKey)
  }, [open, onClose])

  if (!open) return null
  if (typeof document === "undefined") return null

  return createPortal(
    <div className={`fixed inset-0 ${zClassName} flex flex-col justify-end`}>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute inset-0 bg-[#1E1B17]/40"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className="relative flex flex-col rounded-t-[18px] pt-2 pb-[calc(env(safe-area-inset-bottom)+24px)] bg-white shadow-[0_-8px_24px_rgba(30,27,23,0.12)]"
      >
        <div className="flex justify-center pt-1.5 pb-2.5">
          <div className="w-9 h-1 rounded-full bg-[#C9C2B6]" />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}
